import { StrictMode, useEffect, useState, useMemo } from 'react'
import { Provider, useSelector, useDispatch } from 'react-redux'
import { StatsigProvider } from '@statsig/react-bindings'
import { DeviceTypeProvider } from '@calendly/ui/hooks/use-device-type'
import {
  Redirect,
  Router,
  Switch,
  useRouteMatch,
  useHistory,
} from 'react-router-dom'
import { RecaptchaProvider } from '@calendly/recaptcha'
import { FeatureCheckProvider } from '@calendly/feature-check-react'
import { Alert } from '@calendly/alert'
import { ErrorBoundary } from '@calendly/error-boundary'
import {
  Logger,
  LogLevels,
  Airbrake,
  Datadog,
  DatadogRum,
} from '@calendly/logging-service'
import { useBodyStyles, transparentBackground } from '@calendly/ui/body-styles'
import { FormConfigProvider } from '@calendly/form'
import { Loader } from '@calendly/ui/components/loader'
import { parseQuery } from '@calendly/http-tools'
import { usePrevious, useQueryParams } from '@calendly/hooks'
import { GoogleOAuthProvider } from '@react-oauth/google'

import { i18n } from 'locales'
import BookingRoute from 'components/route'
import PublisherToolbar from 'components/publisher_toolbar'
import SelectEventTypePage from 'components/select_event_type'
import {
  ConfirmationPage,
  MeetingPollConfirmation,
} from 'components/confirmation'
import { ConfirmationPageDataWrapper } from 'components/confirmation/useConfirmationPageData'
import SelectDateTimePage from 'components/select_date_time'
import EnterDetailsPage from 'components/enter_details'
import CancellationPage from 'components/cancellation'
import { SchedulingLinkPage } from 'components/scheduling_links'
import PackagePage from 'components/package'
import PackageSuccessPage from 'components/package/success'
import InvoicePage from 'components/invoice'
import { InvoiceDataProvider } from 'components/invoice/InvoiceContext'
import { useInvoiceQuery } from 'components/invoice/useInvoiceQuery'
import Analytics from 'components/analytics'
import { schedulingLinkSelector } from 'selectors/event_type'
import {
  eventTypeSelector,
  rootApi,
  useGetInitialSettingsQuery,
  useGetExperimenterConfigQuery,
  useGetEventTypeQuery,
} from 'root-api'
import { GoogleAnalyticsScript } from 'components/google_analytics'
import { FacebookPixelScript } from 'components/facebook_pixel'
import PublisherTracker from 'components/publisher_tracker'
import Layout from 'components/layout'
import Wrapper from 'components/wrapper'
import { SegmentScript } from 'components/segment'
import { CalAnonymousId } from 'components/cal_anonymous_id'
import { Settings } from 'services/settings'
import ErrorPage from 'components/error/page'
import { currentUserSelector, calAnonymousIdSelector } from 'selectors/user'
import { isEmbeddedSelector } from 'selectors/embed'
import {
  setReferrerPageType,
  setAnalyticsBookingMethod,
  setInviteeFrom,
} from 'actions/analytics'
import ErrorAlert from 'components/error/alert'
import { EmbedResizeMessager } from 'components/embed_resize_messager/embed_resize_messager'
import { EmbedPrefillMessageListener } from 'components/embed_prefill_message_listener/embed_prefill_message_listener'
import { useEventType } from 'components/use_event_type'
import { useSchedulingLink } from 'components/use_scheduling_link'
import { useProfile } from 'components/use_profile'
import { setCurrentUser } from 'actions/current_user'
import { initializeTracking } from 'actions/tracking'
import { useLandingPage } from 'components/use_landing_page'
import { TelemetryProvider, useTelemetry } from 'contexts/TelemetryContext'
import { usePurchasedPackageData } from 'hooks/usePurchasedPackageData'
import RepurchasePackagePage from 'components/repurchase_package'
import MeetingPackageCanceled from 'components/meeting_package_canceled'
import { usePackageForLocale } from 'components/package/usePackageForLocale'
import { usePurchasedPackageForLocale } from 'components/package/usePurchasedPackageForLocale'
import {
  PackageDataProvider,
  PurchasedPackageDataProvider,
} from 'components/package/PackageContext'

import { useSendSegmentEvent } from './hooks/use-segment-events'
import { useInvitee } from './components/use_invitee'
import { useRoutingForm } from './components/use_routing_form'
import { applyRumSamplingLimit } from './utils'
import { isConferencingLocation } from './services/location'
import { anonymousIdSelector } from './selectors/segment'
import { useFeatures } from './components/use_organization'
import { calendarStatusSelector, countryCodeSelector } from './selectors'
import { IdentityProvider, useIdentityReady } from './contexts/IdentityContext'

const bookingBreakpoints = {
  isPhone: { maxWidth: 649 },
  isTabletDown: { maxWidth: 999 },
  isTablet: { minWidth: 650, maxWidth: 999 },
  isTabletUp: { minWidth: 650 },
  isDesktopUp: { minWidth: 1000 },
}

const referrerPageType = (profile) => {
  if (profile.is_landing_page) {
    return 'default_landing_page'
  }
  if (profile.owner_type === 'Page') {
    return 'team_page'
  }
  return 'profile_page'
}

const useGetExperimenterConfig = () => {
  const eventType = useSelector(eventTypeSelector)
  const currentUser = useSelector(currentUserSelector)
  const calAnonymousId = useSelector(calAnonymousIdSelector)
  const isIdentityReady = useIdentityReady()

  return useGetExperimenterConfigQuery(
    {
      organizationUuid: eventType?.organization?.uuid,
      locale: eventType?.locale,
      ...(currentUser?.uuid && { userId: currentUser.uuid }),
      ...(calAnonymousId && { anonymousId: calAnonymousId }),
    },
    {
      skip: !isIdentityReady,
    },
  )
}

const AfterLoad = ({ children }) => {
  const { data: experimentConfig } = useGetExperimenterConfig()
  const isIdentityReady = useIdentityReady()

  if (!isIdentityReady || !experimentConfig) return null

  const { enabledFeatures, isAuthenticated, layers } = experimentConfig

  return (
    <>
      <PublisherTracker />
      <FeatureCheckProvider
        enabledFeatures={enabledFeatures}
        layers={layers}
        isAuthenticated={isAuthenticated}
      >
        <PublisherToolbar />
        <Wrapper>{children}</Wrapper>
      </FeatureCheckProvider>
    </>
  )
}

const WithInvitee = ({ children, bookingPageType }) => {
  const invitee = useInvitee()
  const {
    data: eventType,
    isLoading,
    isError,
  } = useGetEventTypeQuery(
    { shareUuid: invitee?.scheduling_link?.owner_uuid },
    { skip: !invitee || invitee.scheduling_link.owner_type !== 'Share' },
  )
  const dispatch = useDispatch()
  const {
    booking_method: analyticsBookingMethod,
    share_attribution: analyticsShareAttribution,
  } = useQueryParams()
  const sendSegmentEvent = useSendSegmentEvent()
  const user = useSelector(currentUserSelector) || {}

  useEffect(() => {
    if (invitee) i18n.locale = invitee.locale
  }, [invitee])

  useEffect(() => {
    if (eventType && bookingPageType) {
      sendSegmentEvent(
        'event_type',
        eventType.owning_user,
        eventType.id,
        bookingPageType,
        eventType.unavailability_reason?.code,
      )
    }
  }, [eventType, bookingPageType, sendSegmentEvent])

  useEffect(() => {
    if (
      eventType
      && bookingPageType
      && analyticsBookingMethod === 'schedule_in_real_time'
      && analyticsShareAttribution === 'reschedule'
    ) {
      dispatch(setAnalyticsBookingMethod(analyticsBookingMethod))
      dispatch(setInviteeFrom(analyticsShareAttribution))
    }
  }, [
    dispatch,
    analyticsBookingMethod,
    analyticsShareAttribution,
    eventType,
    bookingPageType,
  ])

  if (isLoading || invitee === undefined) return <Loader />

  if (isError || invitee === null) {
    return (
      <ErrorAlert error={{}} buttonClick={() => window.location.reload()} />
    )
  }

  if (eventType?.unavailability_reason && bookingPageType !== 'cancellation') {
    return (
      <ErrorPage current_user={user} error={eventType.unavailability_reason} />
    )
  }

  return <AfterLoad>{children}</AfterLoad>
}

const WithEventType = ({ children, bookingPageType }) => {
  const eventType = useEventType()
  const sendSegmentEvent = useSendSegmentEvent()
  const dispatch = useDispatch()
  const user = useSelector(currentUserSelector) || {}
  const prevEventType = usePrevious(eventType)
  const prevBookingPageType = usePrevious(bookingPageType)
  const history = useHistory()
  const profile = useProfile()
  const {
    purchasedPackage,
    remainingSessions,
    loading: isFetchingPackageData,
    error: isPackageDataError,
  } = usePurchasedPackageData(eventType?.uuid)
  const isPackageCanceled = Boolean(
    purchasedPackage && purchasedPackage.canceled_at
  )

  useEffect(() => {
    if (
      eventType
      && (prevEventType?.id !== eventType.id
        || bookingPageType !== prevBookingPageType)
    ) {
      if (document.referrer === '') {
        dispatch(setReferrerPageType(''))
      }
      sendSegmentEvent(
        'event_type',
        eventType.owning_user,
        eventType.id,
        bookingPageType,
        eventType.unavailability_reason?.code,
      )
      i18n.locale = eventType.locale
    }
  }, [
    eventType,
    dispatch,
    bookingPageType,
    sendSegmentEvent,
    prevEventType,
    prevBookingPageType,
  ])

  useEffect(() => {
    if (isPackageDataError) {
      const url = new URL(window.location.href)
      url.searchParams.delete('redeem')
      history.replace(url.pathname + url.search)
    }
  }, [isPackageDataError, history])

  if (eventType === undefined || isFetchingPackageData || isPackageDataError) {
    return <Loader />
  }

  if (eventType === null) {
    return (
      <ErrorAlert error={{}} buttonClick={() => window.location.reload()} />
    )
  }

  if (eventType.unavailability_reason) {
    return (
      <ErrorPage current_user={user} error={eventType.unavailability_reason} />
    )
  }

  if (isPackageCanceled) {
    return <MeetingPackageCanceled hostName={profile?.name} />
  }

  if (purchasedPackage && remainingSessions === 0) {
    return (
      <RepurchasePackagePage
        purchasedPackage={purchasedPackage}
        eventType={eventType}
      />
    )
  }

  return <AfterLoad>{children}</AfterLoad>
}

const WithProfile = ({ children }) => {
  const profile = useProfile()
  const sendSegmentEvent = useSendSegmentEvent()
  const dispatch = useDispatch()
  const user = useSelector(currentUserSelector) || {}

  useEffect(() => {
    if (profile) i18n.locale = profile.locale
  }, [profile, dispatch])

  useEffect(() => {
    if (!profile || profile.unavailability_reason) return

    const pageType = referrerPageType(profile)
    dispatch(setReferrerPageType(pageType))
    sendSegmentEvent('profile', profile.owning_user, profile.id, pageType)
  }, [sendSegmentEvent, profile, dispatch])

  if (profile === undefined) return <Loader />

  if (profile === null) {
    return (
      <ErrorAlert error={{}} buttonClick={() => window.location.reload()} />
    )
  }

  if (profile.unavailability_reason) {
    return (
      <ErrorPage current_user={user} error={profile.unavailability_reason} />
    )
  }

  return <AfterLoad>{children}</AfterLoad>
}

const WithSchedulingLinkLoader = ({ children }) => {
  const schedulingLink = useSchedulingLink()
  const user = useSelector(currentUserSelector) || {}
  const { isLoading, isError } = useGetEventTypeQuery(
    { shareUuid: schedulingLink?.owner_uuid },
    { skip: !schedulingLink || schedulingLink.owner_type !== 'Share' },
  )
  const { path } = useRouteMatch()
  const isConfirmation = path === '/d/:linkUid/:eventTypeName/invitees/:inviteeUuid'

  if (schedulingLink === undefined || isLoading) return <Loader />

  if (schedulingLink === null || isError) {
    return (
      <ErrorAlert error={{}} buttonClick={() => window.location.reload()} />
    )
  }

  if (schedulingLink.unavailability_reason && !isConfirmation) {
    return (
      <ErrorPage
        current_user={user}
        error={schedulingLink.unavailability_reason}
      />
    )
  }

  return children
}

const WithSchedulingLink = ({ children }) => {
  const schedulingLink = useSelector(schedulingLinkSelector)
  const user = useSelector(currentUserSelector) || {}
  const eventType = useSelector(eventTypeSelector)
  const sendSegmentEvent = useSendSegmentEvent()
  const landingPage = useLandingPage()
  const routingForm = useRoutingForm()
  const dispatch = useDispatch()

  useEffect(() => {
    if (schedulingLink.owner_type === 'Share') {
      sendSegmentEvent(
        'event_type',
        eventType.owning_user,
        eventType.id,
        'availability',
        eventType.unavailability_reason?.code,
      )
    }
  }, [sendSegmentEvent, eventType, schedulingLink])

  useEffect(() => {
    if (eventType) i18n.locale = eventType.locale
  }, [eventType, dispatch])

  const unavailabilityReason = (eventType || landingPage || routingForm)
    ?.unavailability_reason
  if (unavailabilityReason) {
    return <ErrorPage current_user={user} error={unavailabilityReason} />
  }

  return <AfterLoad>{children}</AfterLoad>
}

const WithPackage = ({ children }) => {
  const { loading, eventType, packageData, profile, features } = usePackageForLocale()

  useEffect(() => {
    if (eventType?.locale) i18n.locale = eventType.locale
  }, [eventType])

  if (loading) return <Loader />

  return (
    <PackageDataProvider value={{ packageData, profile, features }}>
      <AfterLoad>{children}</AfterLoad>
    </PackageDataProvider>
  )
}

const WithPurchasedPackage = ({ children }) => {
  const { loading, eventType, purchasedPackage } = usePurchasedPackageForLocale()

  useEffect(() => {
    if (eventType?.locale) i18n.locale = eventType.locale
  }, [eventType])

  if (loading) return <Loader />

  return (
    <PurchasedPackageDataProvider value={purchasedPackage}>
      <AfterLoad>{children}</AfterLoad>
    </PurchasedPackageDataProvider>
  )
}

const WithInvoice = ({ children }) => {
  const { loading, invoice, error, setInvoice } = useInvoiceQuery()

  if (loading) return <Loader />

  if (error || !invoice) {
    return (
      <ErrorAlert
        error={{ message: error?.message || 'Unable to load invoice.' }}
        buttonClick={() => window.location.reload()}
      />
    )
  }

  return (
    <InvoiceDataProvider value={{ invoice, setInvoice }}>
      <AfterLoad>{children}</AfterLoad>
    </InvoiceDataProvider>
  )
}

const Routes = () => {
  const theme = parseQuery(window.location.search)
  const { data } = useFeatures()
  const hasCustomBackground = data?.custom_theme_allowed && Boolean(theme.background_color)

  return (
    <FormConfigProvider value={{ colorSafe: hasCustomBackground }}>
      <GoogleAnalyticsScript />
      <FacebookPixelScript />
      <Layout>
        <Switch>
          <BookingRoute path="/:profileSlug" exact>
            <WithProfile>
              <SelectEventTypePage />
            </WithProfile>
          </BookingRoute>
          <BookingRoute path="/reschedulings/:inviteeUuid/confirmation" exact>
            <WithInvitee bookingPageType="confirmation">
              <ConfirmationPage />
            </WithInvitee>
          </BookingRoute>
          <BookingRoute path="/reschedulings/:inviteeUuid/:spotStartTime" exact>
            <WithInvitee bookingPageType="invitee_details">
              <EnterDetailsPage />
            </WithInvitee>
          </BookingRoute>
          <BookingRoute path="/reschedulings/:inviteeUuid" exact>
            <WithInvitee bookingPageType="availability">
              <SelectDateTimePage />
            </WithInvitee>
          </BookingRoute>
          <BookingRoute path="/cancellations/:inviteeUuid" exact>
            <WithInvitee bookingPageType="cancellation">
              <CancellationPage />
            </WithInvitee>
          </BookingRoute>
          <BookingRoute path="/s/:singleUseSlug" exact>
            <WithEventType bookingPageType="availability">
              <SelectDateTimePage />
            </WithEventType>
          </BookingRoute>
          <BookingRoute path="/s/:singleUseSlug/:spotStartTime" exact>
            <WithEventType bookingPageType="invitee_details">
              <EnterDetailsPage />
            </WithEventType>
          </BookingRoute>
          <Redirect
            from="/b/:profileSlug/:eventTypeSlug/invitees/:inviteeUuid"
            to="/:profileSlug/:eventTypeSlug/invitees/:inviteeUuid"
          />
          <BookingRoute
            path="/b/:profileSlug/:eventTypeSlug/:spotStartTime"
            exact
          >
            <WithEventType bookingPageType="invitee_details">
              <EnterDetailsPage />
            </WithEventType>
          </BookingRoute>
          <BookingRoute path="/b/:profileSlug/:eventTypeSlug" exact>
            <WithEventType bookingPageType="availability">
              <SelectDateTimePage />
            </WithEventType>
          </BookingRoute>
          <BookingRoute path="/d/:linkUid/:eventTypeName/confirmation" exact>
            <WithSchedulingLinkLoader>
              <WithSchedulingLink>
                <ConfirmationPageDataWrapper>
                  <MeetingPollConfirmation />
                </ConfirmationPageDataWrapper>
              </WithSchedulingLink>
            </WithSchedulingLinkLoader>
          </BookingRoute>
          <BookingRoute
            path="/d/:linkUid/:eventTypeName/invitees/:inviteeUuid"
            exact
          >
            <WithSchedulingLinkLoader>
              <WithInvitee bookingPageType="confirmation">
                <ConfirmationPage />
              </WithInvitee>
            </WithSchedulingLinkLoader>
          </BookingRoute>
          <BookingRoute path="/d/:linkUid/:eventTypeName/:spotStartTime" exact>
            <WithSchedulingLinkLoader>
              <WithSchedulingLink>
                <EnterDetailsPage />
              </WithSchedulingLink>
            </WithSchedulingLinkLoader>
          </BookingRoute>
          <BookingRoute path="/d/:linkUid/:eventTypeName">
            <WithSchedulingLinkLoader>
              <WithSchedulingLink>
                <SchedulingLinkPage />
              </WithSchedulingLink>
            </WithSchedulingLinkLoader>
          </BookingRoute>
          <BookingRoute path="/d/:linkUid">
            <WithSchedulingLinkLoader>
              <WithSchedulingLink>
                <SchedulingLinkPage />
              </WithSchedulingLink>
            </WithSchedulingLinkLoader>
          </BookingRoute>
          <BookingRoute path="/c/:eventTypeUuid/invitees/:inviteeUuid" exact>
            <WithInvitee bookingPageType="confirmation">
              <ConfirmationPage />
            </WithInvitee>
          </BookingRoute>
          <BookingRoute path="/c/:eventTypeUuid/:spotStartTime" exact>
            <WithEventType bookingPageType="invitee_details">
              <EnterDetailsPage />
            </WithEventType>
          </BookingRoute>
          <BookingRoute path="/c/:eventTypeUuid" exact>
            <WithEventType bookingPageType="availability">
              <SelectDateTimePage />
            </WithEventType>
          </BookingRoute>
          <BookingRoute
            path="/:profileSlug/packages/:packageUuid/purchase/:purchasedPackageUuid"
            exact
          >
            <WithPurchasedPackage>
              <PackageSuccessPage />
            </WithPurchasedPackage>
          </BookingRoute>
          <BookingRoute path="/:profileSlug/packages/:packageUuid" exact>
            <WithPackage>
              <PackagePage />
            </WithPackage>
          </BookingRoute>
          <BookingRoute
            path="/:profileSlug/payments/:packageUuid/purchase/:purchasedPackageUuid"
            exact
          >
            <WithPurchasedPackage>
              <PackageSuccessPage />
            </WithPurchasedPackage>
          </BookingRoute>
          <BookingRoute path="/:profileSlug/payments/:packageUuid" exact>
            <WithPackage>
              <PackagePage />
            </WithPackage>
          </BookingRoute>
          <BookingRoute path="/:profileSlug/invoices/:invoiceUuid" exact>
            <WithInvoice>
              <InvoicePage />
            </WithInvoice>
          </BookingRoute>
          <BookingRoute
            path="/:profileSlug/:eventTypeSlug/invitees/:inviteeUuid"
            exact
          >
            <WithInvitee bookingPageType="confirmation">
              <ConfirmationPage />
            </WithInvitee>
          </BookingRoute>
          <BookingRoute
            path="/:profileSlug/:eventTypeSlug/:spotStartTime"
            exact
          >
            <WithEventType bookingPageType="invitee_details">
              <EnterDetailsPage />
            </WithEventType>
          </BookingRoute>
          <BookingRoute path="/:profileSlug/:eventTypeSlug" exact>
            <WithEventType bookingPageType="availability">
              <SelectDateTimePage />
            </WithEventType>
          </BookingRoute>
        </Switch>
      </Layout>
    </FormConfigProvider>
  )
}

const RoutesWrapper = ({
  history,
  googleClientId,
  siteKey,
  isActive,
  siteKeyV2,
  isActiveV2,
  onCalAnonymousIdInitialized,
}) => (
  <GoogleOAuthProvider clientId={googleClientId}>
    <EmbedResizeMessager>
      <EmbedPrefillMessageListener />
      <SegmentScript />
      <CalAnonymousId onInitialized={onCalAnonymousIdInitialized} />
      <Alert />
      <RecaptchaProvider
        siteKey={siteKey}
        isActive={isActive}
        siteKeyV2={siteKeyV2}
        isActiveV2={isActiveV2}
      >
        <Router history={history}>
          <Routes />
        </Router>
        <Analytics />
      </RecaptchaProvider>
    </EmbedResizeMessager>
  </GoogleOAuthProvider>
)

const StatsigWrapper = ({
  isSettingsReady,
  isEmbedded,
  eventType,
  user,
  anonymousId,
  calAnonymousId,
  children,
}) => {
  const calendarStatus = useSelector(calendarStatusSelector)
  const countryCode = useSelector(countryCodeSelector)
  // 'pending' means the event type query is in-flight; 'uninitialized' means
  // no event type is needed on this page (e.g. profile page) so we don't block.
  // 'rejected' must also be treated as unresolved -- if we allowed StatsigProvider
  // to mount on failure it would toggle the tree structure (no-wrapper -> wrapper),
  // causing the entire subtree to remount, RTK to resubscribe, and the failed
  // query to refetch, producing an infinite loop.
  const isEventTypeUnresolved = useSelector(
    (state) => {
      const { status } = rootApi.endpoints.getEventType.select()(state)
      return status === 'pending' || status === 'rejected'
    }
  )
  const [isAvailableToReload, setIsAvailableToReload] = useState(false)
  const { owning_user: owningUser = {}, organization } = eventType || {}
  const { id: eventTypeHostId, uuid: eventTypeHostUuid } = owningUser
  const { uuid: organizationUuid } = organization || {}

  const statsigUser = useMemo(() => {
    const customIDs = {}
    if (user) customIDs.user_uuid = user.uuid
    if (eventTypeHostId) customIDs.host_id = eventTypeHostId
    if (eventTypeHostUuid) customIDs.host_uuid = eventTypeHostUuid
    if (anonymousId) customIDs.stableID = anonymousId
    if (calAnonymousId) customIDs.cal_anonymous_id = calAnonymousId
    if (organizationUuid) {
      customIDs.organization_uuid = organizationUuid
      // For backward compatibility. Ideally, organization_uuid should be used.
      customIDs.Organization = organizationUuid
    }

    const locationConfigs = eventType?.location_configurations || []
    const locationCustom = eventType ? {
      event_type_has_conferencing: locationConfigs.some((c) => isConferencingLocation(c?.kind)),
      ...(locationConfigs.length === 1 && { event_type_location_type: locationConfigs[0]?.kind }),
      ...(locationConfigs.length > 1 && { event_type_location_type: 'multiple' }),
    } : {}

    const custom = {
      ...locationCustom,
      ...(countryCode && { country_code: countryCode }),
    }

    return {
      ...(user && { userID: user.uuid }),
      customIDs,
      custom,
    }
  }, [
    user,
    eventTypeHostId,
    eventTypeHostUuid,
    anonymousId,
    calAnonymousId,
    organizationUuid,
    eventType,
    countryCode,
  ])

  const statsigOptions = useMemo(
    () => ({
      environment: {
        tier: Settings?.statsig?.environment_tier,
      },
    }),
    [],
  )

  useEffect(() => {
    if (calAnonymousId && !isAvailableToReload) {
      // When no calendar is rendered (undefined), enable immediately.
      // When a calendar is rendered, wait until it finishes loading to avoid a visual flash.
      if (calendarStatus === undefined || calendarStatus !== 'loading') {
        setIsAvailableToReload(true)
      }
    }
  }, [calAnonymousId, calendarStatus, isAvailableToReload])

  if (
    !isSettingsReady
    || !Settings?.statsig?.client_key
    || !Settings?.statsig?.environment_tier
    || !isAvailableToReload
    || isEmbedded
    || isEventTypeUnresolved
  ) {
    return children
  }

  return (
    <StatsigProvider
      sdkKey={Settings.statsig.client_key}
      user={statsigUser}
      options={statsigOptions}
    >
      {children}
    </StatsigProvider>
  )
}

const ContentWrapper = ({ history }) => {
  const user = useSelector(currentUserSelector)
  const anonymousId = useSelector(anonymousIdSelector)
  const calAnonymousId = useSelector(calAnonymousIdSelector)
  const isEmbedded = useSelector(isEmbeddedSelector)
  const eventType = useSelector(eventTypeSelector)
  const { data, isLoading, isError } = useGetInitialSettingsQuery()
  const [isSettingsReady, setIsSettingsReady] = useState(false)
  const [isUserReady, setIsUserReady] = useState(false)
  const [isCalAnonymousIdReady, setIsCalAnonymousIdReady] = useState(false)
  const [isTrackingReady, setIsTrackingReady] = useState(false)
  const dispatch = useDispatch()
  const { refreshTelemetry } = useTelemetry()

  const isIdentityReady = isUserReady && isCalAnonymousIdReady

  const handleCalAnonymousIdInitialized = () => {
    setIsCalAnonymousIdReady(true)
  }

  const routesWrapper = useMemo(() => {
    if (!isSettingsReady) return null

    return (
      <RoutesWrapper
        history={history}
        googleClientId={Settings.google_client_id}
        siteKey={Settings.recaptcha.site_key}
        isActive={Settings.recaptcha.activated}
        siteKeyV2={Settings.recaptcha.site_key_v2}
        isActiveV2={Settings.recaptcha.booking.activated}
        onCalAnonymousIdInitialized={handleCalAnonymousIdInitialized}
      />
    )
  }, [history, isSettingsReady])

  useEffect(() => {
    dispatch(initializeTracking())
    setIsTrackingReady(true)
  }, [dispatch])

  useEffect(() => {
    if (data) {
      // Limit RUM sampling to avoid inflated metrics
      const appSettings = applyRumSamplingLimit(data.settings)

      Settings.set(appSettings)
      Logger.initialize({
        config: {
          environment: window.APP_ENV,
          version: window.APP_VERSION,
          logLevel: LogLevels.debug,
        },
        options: appSettings,
        transports: [console, Airbrake, Datadog, DatadogRum],
      })
      setIsSettingsReady(true)
    }
  }, [data])

  useEffect(() => {
    if (data) {
      const currentUser = data.session.current || {}
      i18n.locale = data.request.locale
      dispatch(setCurrentUser(currentUser))
      setIsUserReady(true)
    }
  }, [data, dispatch])

  useEffect(() => {
    if (isSettingsReady) {
      refreshTelemetry()
    }
  }, [isSettingsReady, refreshTelemetry])

  if (isLoading || !isTrackingReady || !isSettingsReady || !isUserReady) {
    return <Loader />
  }

  if (isError) {
    return (
      <ErrorAlert error={{}} buttonClick={() => window.location.reload()} />
    )
  }

  return (
    <IdentityProvider value={isIdentityReady}>
      <StatsigWrapper
        isSettingsReady={isSettingsReady}
        isEmbedded={isEmbedded}
        eventType={eventType}
        user={user}
        anonymousId={anonymousId}
        calAnonymousId={calAnonymousId}
      >
        {routesWrapper}
      </StatsigWrapper>
    </IdentityProvider>
  )
}

export const App = ({ store, history }) => {
  useBodyStyles({ background: transparentBackground })
  const query = parseQuery(window.location.search)

  if (query?.closeOnLoad) {
    // Used to close tabs opened by Stripe PaymentElement
    window.close()
  }

  return (
    <StrictMode>
      <ErrorBoundary logger={Logger}>
        <DeviceTypeProvider value={{ breakpoints: bookingBreakpoints }}>
          <Provider store={store}>
            <TelemetryProvider>
              <ContentWrapper history={history} />
            </TelemetryProvider>
          </Provider>
        </DeviceTypeProvider>
      </ErrorBoundary>
    </StrictMode>
  )
}
